"use client";

import { Alert } from "@/src/components";
import { ComponentDoc, type PropDefinition, type ExampleDefinition } from "../../_components";

// Define props once
const alertProps: PropDefinition[] = [
  {
    name: "variant",
    type: '"default" | "destructive" | "success" | "warning"',
    default: '"default"',
    description: "Visual style variant",
  },
  {
    name: "title",
    type: "string",
    description: "Alert heading text",
  },
  {
    name: "description",
    type: "string",
    description: "Alert description text",
  },
];

// Define examples once
const examples: ExampleDefinition[] = [
  {
    title: "Default",
    description: "Informational alert for general messages",
    preview: <Alert title="Heads up!" description="You can add components to your app using the CLI." />,
    code: `<Alert
  title="Heads up!"
  description="You can add components to your app using the CLI."
/>`,
  },
  {
    title: "Destructive",
    description: "For errors and critical warnings",
    preview: <Alert variant="destructive" title="Error" description="Your session has expired. Please log in again." />,
    code: `<Alert
  variant="destructive"
  title="Error"
  description="Your session has expired. Please log in again."
/>`,
  },
  {
    title: "Success",
    description: "For successful operations",
    preview: <Alert variant="success" title="Success" description="Your changes have been saved successfully." />,
    code: `<Alert
  variant="success"
  title="Success"
  description="Your changes have been saved successfully."
/>`,
  },
  {
    title: "Warning",
    description: "For cautionary messages",
    preview: <Alert variant="warning" title="Warning" description="Your subscription will expire in 3 days." />,
    code: `<Alert
  variant="warning"
  title="Warning"
  description="Your subscription will expire in 3 days."
/>`,
  },
];

export default function DocsComponentsAlert() {
  return (
    <ComponentDoc
      name="Alert"
      description="Alerts display important messages that require the user's attention. Use them sparingly for critical information."
      importPath="@/registry/new-york/ui/alert"
      importNames={["Alert"]}
      props={alertProps}
      examples={examples}
      accessibility={[
        'Uses role="alert" for screen reader announcement',
        "Includes appropriate icons for each variant",
        "Color is not the only indicator of meaning (icons + text)",
        "Sufficient color contrast for all variants",
      ]}
      dos={[
        "Use for important, time-sensitive information",
        "Keep messages clear and actionable",
        "Use appropriate variant for the message type",
        "Include both title and description for clarity",
      ]}
      donts={[
        "Don't use for non-critical information",
        "Don't stack multiple alerts",
        "Don't use destructive variant for warnings",
        "Don't make alerts dismissible without user action",
      ]}
    />
  );
}
